/**
 * MutualAidBoard — Apóyate SB (Sobrato & Destination Home grants).
 *
 * Two surfaces, matching the LCC requirements:
 *
 *  • Pods — WhatsApp mutual-aid network enrollment across the 4 pods
 *    (Food / Housing / Medical / General Support). Shows per-pod counts AND the
 *    DE-DUPLICATED unique-participant total (a person in multiple pods counts
 *    once — the prior organizer double-counted; this is the grant's headline
 *    metric, target 1,000 Yr1). Pod membership lives on the member's
 *    pod_assignment multi_select field; counts come from /pod-participants.
 *
 *  • Requests & Exchanges — mutual-aid INTERACTIONS (requests made on
 *    WhatsApp/Signal, fulfilled or not; target 100–200) and EXCHANGES (an
 *    actual handoff between giver and receiver). Type / status / channel /
 *    responder count / outcome / community quote per request.
 *
 * Backend: app/routers/mutual_aid.py via services/api/mutualAid.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Heart, Filter, Plus, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MutualAidRequest,
  MutualAidRequestType,
  MutualAidStatus,
  ReportResult,
  listRequests,
  createRequest,
  updateRequest,
  createExchange,
  getPodParticipants,
  getSummary,
} from '@/services/api/mutualAid';

const POD_TARGET = 1000; // Year 1 enrollment goal
const UNIQUE_ROW = 'ALL PODS (unique)';

const STATUS_OPTIONS: MutualAidStatus[] = [
  'open', 'in_progress', 'fulfilled', 'partially_fulfilled', 'unfulfilled', 'closed',
];
const STATUS_VARIANT: Record<MutualAidStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  open: 'default', in_progress: 'default', fulfilled: 'secondary',
  partially_fulfilled: 'secondary', unfulfilled: 'destructive', closed: 'outline',
};
const TYPE_OPTIONS: MutualAidRequestType[] = [
  'food', 'housing', 'medical', 'transportation', 'financial', 'general', 'other',
];
const TYPE_LABEL: Record<string, string> = {
  food: 'Food', housing: 'Housing', medical: 'Medical', transportation: 'Transportation',
  financial: 'Financial', general: 'General', other: 'Other',
};
const CHANNELS = ['WhatsApp', 'Signal', 'Phone', 'In-person', 'SMS'];

type Tab = 'pods' | 'requests';

export const MutualAidBoard: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pods');
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b">
        {(['pods', 'requests'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? 'border-rose-500 text-rose-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'pods' ? 'Pods & Enrollment' : 'Requests & Exchanges'}
          </button>
        ))}
      </div>
      {tab === 'pods' ? <PodsPanel /> : <RequestsPanel />}
    </div>
  );
};

// ───────────────────────────── Pods ─────────────────────────────

const PodsPanel: React.FC = () => {
  const [report, setReport] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPodParticipants()
      .then((res) => !cancelled && setReport(res))
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load pod enrollment.'));
    return () => { cancelled = true; };
  }, []);

  if (error) return <Card><CardContent className="p-6 text-sm text-red-600">{error}</CardContent></Card>;
  if (!report) return <Card><CardContent className="p-6 text-sm text-gray-500">Loading pod enrollment…</CardContent></Card>;

  const rows = report.rows || [];
  const unique = Number(rows.find((r) => r.pod === UNIQUE_ROW)?.unique_participants || 0);
  const pods = rows.filter((r) => r.pod !== UNIQUE_ROW);
  const pct = Math.min(100, Math.round((unique / POD_TARGET) * 100));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Heart className="h-4 w-4" /> Unique participants (de-duplicated)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-3xl font-semibold">{unique.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">
            of {POD_TARGET.toLocaleString()} target (Year 1) · a person in multiple pods counts once
          </div>
          <div className="mt-2 h-2 w-full rounded bg-gray-100 overflow-hidden">
            <div className="h-2 bg-rose-500" style={{ width: `${pct}%` }} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pods</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {pods.length === 0 ? (
            <div className="text-sm text-gray-500 py-3">
              No pod memberships yet. Tag members' <span className="font-medium">Pod Assignment</span> field
              (or collect it via the public intake form) and counts appear here.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pods.map((p) => (
                <div key={p.pod} className="flex items-center justify-between rounded border px-3 py-2">
                  <span className="text-sm truncate">{p.pod}</span>
                  <Badge variant="outline">{Number(p.unique_participants).toLocaleString()}</Badge>
                </div>
              ))}
            </div>
          )}
          <div className="text-xs text-gray-400 pt-3">
            Per-pod counts sum higher than the unique total when members belong to multiple pods.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────── Requests & Exchanges ───────────────────────

const RequestsPanel: React.FC = () => {
  const [rows, setRows] = useState<MutualAidRequest[] | null>(null);
  const [summary, setSummary] = useState<ReportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  // New-request form
  const [showForm, setShowForm] = useState(false);
  const [newType, setNewType] = useState<MutualAidRequestType>('food');
  const [newChannel, setNewChannel] = useState('WhatsApp');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(null); setError(null);
    listRequests({ request_type: typeFilter || undefined, status: statusFilter || undefined })
      .then((d) => !cancelled && setRows(d))
      .catch((e: any) => !cancelled && setError(e?.message || 'Failed to load requests.'));
    getSummary().then((s) => !cancelled && setSummary(s)).catch(() => {});
    return () => { cancelled = true; };
  }, [typeFilter, statusFilter, reloadKey]);

  const totals = useMemo(() => {
    const r = summary?.rows || [];
    return {
      requests: r.reduce((a, x) => a + Number(x.request_count || 0), 0),
      exchanges: r.reduce((a, x) => a + Number(x.exchange_count || 0), 0),
      fulfilled: r.filter((x) => x.status === 'fulfilled').reduce((a, x) => a + Number(x.request_count || 0), 0),
    };
  }, [summary]);

  const addRequest = async () => {
    setSaving(true);
    try {
      await createRequest({ request_type: newType, channel: newChannel, status: 'open' });
      setShowForm(false); setNewType('food'); setNewChannel('WhatsApp');
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to log request.');
    } finally { setSaving(false); }
  };

  const changeStatus = async (id: number, status: MutualAidStatus) => {
    try { await updateRequest(id, { status }); setReloadKey((k) => k + 1); }
    catch (e: any) { setError(e?.message || 'Failed to update status.'); }
  };

  const logExchange = async (req: MutualAidRequest) => {
    const item = window.prompt('What was exchanged (item / resource)?');
    if (item === null) return;
    try {
      await createExchange(req.id, { item: item || undefined, handoff_completed: true });
      // Reflect the completed handoff on the request status if still open.
      if (req.status === 'open' || req.status === 'in_progress') {
        await updateRequest(req.id, { status: 'fulfilled' });
      }
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to log exchange.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Heart className="h-4 w-4" /> Mutual Aid Requests
            {rows ? <span className="text-sm font-normal text-gray-500">({rows.length})</span> : null}
          </CardTitle>
          <Button size="sm" onClick={() => setShowForm((s) => !s)}>
            <Plus className="h-4 w-4 mr-1" /> Log request
          </Button>
        </div>
        {summary ? (
          <div className="text-xs text-gray-500 pt-1">
            {totals.requests} interactions · {totals.exchanges} exchanges · {totals.fulfilled} fulfilled
            <span className="text-gray-400"> · target 100–200 interactions</span>
          </div>
        ) : null}
        <div className="flex items-center gap-2 pt-2 text-sm">
          <Filter className="h-3 w-3 text-gray-400" />
          <select className="border rounded px-2 py-1 text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        {showForm ? (
          <div className="flex flex-wrap items-end gap-2 pt-3">
            <select className="border rounded px-2 py-1 text-sm" value={newType} onChange={(e) => setNewType(e.target.value as MutualAidRequestType)}>
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
            <select className="border rounded px-2 py-1 text-sm" value={newChannel} onChange={(e) => setNewChannel(e.target.value)}>
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button size="sm" disabled={saving} onClick={addRequest}>{saving ? 'Saving…' : 'Add'}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="pt-0">
        {error ? <div className="text-sm text-red-600 py-2">{error}</div> : null}
        {rows === null ? (
          <div className="text-sm text-gray-500 py-3">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500 py-3">No requests yet. Log one with the button above.</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b py-3 last:border-b-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{TYPE_LABEL[r.request_type] || r.request_type}</Badge>
                  <Badge variant={STATUS_VARIANT[r.status]}>{r.status.replace(/_/g, ' ')}</Badge>
                  {r.channel ? <span className="text-xs text-gray-400">{r.channel}</span> : null}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {r.created_date}
                  {r.responder_count ? ` · ${r.responder_count} responded` : ''}
                  {r.outcome ? ` · ${r.outcome}` : ''}
                </div>
                {r.community_quote ? (
                  <div className="text-xs italic text-gray-400 mt-0.5 truncate">“{r.community_quote}”</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                  onClick={() => logExchange(r)}
                  title="Record an actual resource handoff"
                >
                  <Package className="h-3 w-3" /> exchange
                </button>
                <select
                  className="border rounded px-2 py-1 text-xs"
                  value={r.status}
                  onChange={(e) => changeStatus(r.id, e.target.value as MutualAidStatus)}
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default MutualAidBoard;
